import { useDispatch, useSelector } from 'react-redux'
import { useScrollBottom } from 'hooks/useScrollBottom'
import { BotTyping } from 'components/BotTyping'
import { endBotResponse, messagesActions, messagesSelectors, selectMessages, streamBotResponse } from 'store/messages'
import clsx from 'clsx'
import { useGetBotMode } from 'hooks/useGetBotMode'
import { memo, useEffect, useState } from 'react'
import { settingsActions, settingsSelectors } from 'store/settings'
import { getChatMessages, markChatAsRead } from 'api/chats'
import { TicketReminderCard } from 'components/ui/TicketReminderCard'
import { Spinner } from 'components/ui/Spinner'
import { ChatWebSocketMessage, WebsocketActionsEnum } from 'types/index'
import { store } from 'store/index'
import { MessagesDiv } from 'components/ui/MessageDiv'
import { ChatMessage } from 'types/chat-message'
import { Chat, ChatResponseModeEnum, ChatRoleEnum } from 'types/chat'
import { ChatMessages } from 'components/chats/ChatMessages'
import _ from 'lodash'
import { useGetSetting } from 'api/settings'
import { socketSingleton } from 'services/ws'

const ChatDetailComponent = () => {
  const { setting } = useGetSetting()
  const messages = useSelector(selectMessages)
  const { isAppMode } = useGetBotMode()
  const dispatch = useDispatch()
  const chatId = useSelector(messagesSelectors.chatId)
  const currentChatBotId = useSelector(messagesSelectors.currentChatBotId)
  const [loading, setLoading] = useState(true)
  const bottomRef = useScrollBottom(messages)
  const numberOfQuestionsBeforeAskTicketOpen = useSelector(settingsSelectors.numberOfQuestionsBeforeAskTicketOpen)
  const currentUserId = useSelector(settingsSelectors.userId)

  useEffect(() => {
    if (currentUserId) {
      socketSingleton.connectTo(String(currentUserId), (wsMessage) => {
        if (wsMessage.action === WebsocketActionsEnum.UPDATE_CHAT_PARTICIPANTS) {
          const updatedChat = wsMessage.body as Chat
          store.dispatch(messagesActions.setChat(updatedChat))
        }
      })
    }
    return () => {
      if (currentUserId) {
        socketSingleton.disconnectTo(String(currentUserId))
      }
    }
  }, [currentUserId])

  useEffect(() => {
    if (chatId) {
      socketSingleton.connectTo(String(chatId), (wsMessage: ChatWebSocketMessage) => {
        if (wsMessage.action === WebsocketActionsEnum.NEW_MESSAGE) {
          const message = wsMessage.body as ChatMessage
          if (message?.chatResponseMode === ChatResponseModeEnum.OPERATOR) {
            if (currentUserId && message.senderId !== currentUserId) {
              store.dispatch(messagesActions.addMessage(wsMessage.body))
            }
            store.dispatch(
              messagesActions.updateChat({
                lastMessage: wsMessage.body,
                _id: wsMessage.body.chatId,
              }),
            )
            markChatAsRead(chatId).catch(console.error)
          }
        }
        if (wsMessage.action === WebsocketActionsEnum.STREAM) {
          if (currentChatBotId) {
            console.debug('botMessageId', wsMessage.messageId)
            store.dispatch(
              streamBotResponse({
                botMessageId: wsMessage.messageId,
                partialResponse: wsMessage.body ?? '',
                senderId: currentChatBotId,
              }),
            )
          }
        }
        if (wsMessage.action === WebsocketActionsEnum.END_STREAM) {
          store.dispatch(endBotResponse())
          setTimeout(() => {
            store.dispatch(messagesActions.updateChatLastMessage())
          }, 0)
          const messages = selectMessages(store.getState())
          const botMessages = _.filter(messages, (m) => m.chatSenderRole === ChatRoleEnum.BOT)
          if (botMessages.length % numberOfQuestionsBeforeAskTicketOpen === 0) {
            store.dispatch(settingsActions.setTicketReminderVisible(true))
            setTimeout(() => {
              bottomRef?.current?.scrollIntoView({ behavior: 'smooth' })
            }, 250)
          }
        }
      })
    }

    return () => {
      if (chatId) {
        socketSingleton.disconnectTo(String(chatId))
      }
    }
  }, [chatId, currentUserId])

  useEffect(() => {
    if (chatId) {
      setLoading(true)
      getChatMessages(chatId).then(() => {
        setLoading(false)
        setTimeout(() => {
          bottomRef?.current?.scrollIntoView({ behavior: 'smooth' })
        }, 200)
      })
    }
    return () => {
      dispatch(messagesActions.setMessages([]))
    }
  }, [chatId])

  return (
    <MessagesDiv
      className={clsx(
        'tw-flex tw-w-full tw-h-full tw-flex-col tw-self-start tw-overflow-y-auto tw-rounded-t-3xl tw-bg-primary-500 tw-p-2 tw-pt-2 ',
      )}
      widgetColor={setting?.secondaryColor}
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          <ChatMessages />
          <BotTyping />
          <TicketReminderCard />
          <div ref={bottomRef} />
        </>
      )}
    </MessagesDiv>
  )
}

export const ChatDetail = memo(ChatDetailComponent)
